import { z } from "zod";
import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/http";
import type { TableParamsApi } from "@/lib/table-params";
import { zSpringPage, zTimestamp } from "./_schemas";

export const executionSchema = z.object({
  id: z.string(),
  pipelineId: z.string().nullish(),
  status: z.string(),
  triggerType: z.string().nullish(),
  triggeredBy: z.string().nullish(),
  startedAt: zTimestamp.nullish(),
  finishedAt: zTimestamp.nullish(),
  durationMs: z.number().nullish(),
  recordsRead: z.number().nullish(),
  recordsWritten: z.number().nullish(),
  recordsFailed: z.number().nullish(),
  errorMessage: z.string().nullish(),
});
export type Execution = z.infer<typeof executionSchema>;

export function recentExecutionsQuery(params: { page?: number; size?: number } = {}) {
  const { page = 0, size = 20 } = params;
  return queryOptions({
    queryKey: ["executions", "recent", page, size],
    queryFn: () =>
      apiRequest<unknown>(`/monitoring/executions/recent?page=${page}&size=${size}`).then((r) =>
        zSpringPage(executionSchema).parse(r),
      ),
  });
}

export function useRecentExecutions(params?: { page?: number; size?: number }) {
  return useQuery(recentExecutionsQuery(params));
}

/** Server-paginated + filtered executions for the Executions screen. */
export function useExecutionsPage(params: TableParamsApi) {
  const search = params.toSearchParams().toString();
  return useQuery({
    queryKey: ["executions", "page", search],
    queryFn: () =>
      apiRequest<unknown>(`/monitoring/executions/recent?${search}`).then((r) =>
        zSpringPage(executionSchema).parse(r),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useFailedExecutions(hours = 24) {
  return useQuery({
    queryKey: ["executions", "failed", hours],
    queryFn: () =>
      apiRequest<unknown>(`/monitoring/executions/failed?hours=${hours}`).then((r) =>
        z.array(executionSchema).parse(r),
      ),
  });
}

// ── Dead letters ────────────────────────────────────────────────────────────

export const deadLetterSchema = z.object({
  id: z.string(),
  executionId: z.string().nullish(),
  pipelineId: z.string().nullish(),
  stepName: z.string().nullish(),
  phase: z.string().nullish(),
  errorClass: z.string().nullish(),
  errorMessage: z.string().nullish(),
  recordData: z.string().nullish(),
  skippedAt: zTimestamp.nullish(),
});
export type DeadLetter = z.infer<typeof deadLetterSchema>;

export function useDeadLetters(executionId: string, enabled = true) {
  return useQuery({
    queryKey: ["dead-letters", "execution", executionId],
    enabled,
    queryFn: () =>
      apiRequest<unknown>(`/dead-letters/execution/${executionId}?size=50`).then((r) =>
        zSpringPage(deadLetterSchema).parse(r),
      ),
  });
}
